'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { AccountMenu } from './AccountMenu';
import { GlobalMenu } from './GlobalMenu';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import i18n from '@benzinga/translate';
import { useTranslation } from 'react-i18next';

import { featuredMenu as TopBarMenu } from './Header/_mockMenu';
import Hooks from '@benzinga/hooks';
import { useRouter, usePathname } from 'next/navigation';
import { getRegisterType } from '@benzinga/utils';
import { ButtonBzPro } from './BenzingaProButton';
import { handleMenuItemsClickEvent } from '../utils';
export { featuredMenu as TopBarMenu } from './Header/_mockMenu';

interface MenuItem {
  href: string;
  logged_in_href?: string;
  title: string;
}

interface TopBarProps {
  items?: MenuItem[];
}

export const TopBar = React.memo(({ items }: TopBarProps) => {
  const isLoggedIn = Hooks.useHydrate(useIsUserLoggedIn(), false);
  const { t } = useTranslation('common', { i18n });
  const itemsList = () => {
    const topBarItems = items ? items : TopBarMenu;

    return topBarItems?.map(item => (
      <li className="bz-top-bar-list-item" key={item.title}>
        <a href={item.href} onClick={() => handleMenuItemsClickEvent('menu_click', 'top_bar', item.title)}>
          <div className="bz-top-bar-list-item-label">{t(`${item.title}`)}</div>
        </a>
      </li>
    ));
  };

  const router = useRouter();
  const routerAsPath = usePathname();

  const requestAuth = React.useCallback(
    action => {
      const redirectUrl = new URL(`${window.location.origin}/login?action=${action}&next=${window.location.href}`);

      const utm_source = getRegisterType(routerAsPath ?? '');
      if (utm_source) {
        redirectUrl.searchParams.append('utm_source', utm_source);
      }

      window.location.href = redirectUrl.toString();
    },
    [routerAsPath],
  );

  const login = React.useCallback(() => {
    requestAuth('login');
  }, [requestAuth]);

  const register = React.useCallback(() => {
    requestAuth('register');
  }, [requestAuth]);

  const logout = React.useCallback(() => {
    let next = window.location.href;
    if (next.includes('/account')) {
      next = window.location.origin;
    }
    next = encodeURIComponent(next);

    const url = new URL(`${window.location.origin}/logout?next=${next}`);
    router.push(url.toString());
  }, [router]);

  return (
    <TopBarWrapper className="top-bar-wrapper">
      <div className="bar-layout-width">
        <div className="top-bar-inner-wrapper">
          <ul className="top-bar-list">
            <li className="bz-top-bar-list-item ProLabel">
              <ButtonBzPro
                label="Get Benzinga Pro"
                url="https://www.benzinga.com/pro/register?adType=homepagebluebutton&utm_source=homepage-blue-button&utm_campaign=14daytrial"
                variant="light"
              />
            </li>
            {itemsList()}
          </ul>
          <div className="auth-buttons">
            <div className="contributor-link">
              <a href="https://contributor.benzinga.com/contributor-onboarding">{t('Navigation.TopBar.contributor')}</a>
            </div>
            <GlobalMenu />
            {isLoggedIn ? (
              <React.Suspense>
                <AccountMenu deviceType="desktop" isLoggedIn={isLoggedIn} onLogout={logout} />
              </React.Suspense>
            ) : (
              <>
                <button className="auth-link-button" onClick={login}>
                  {t('Navigation.TopBar.login')}
                </button>
                <button className="auth-link-button bg-orange-600" onClick={register}>
                  {t('Navigation.TopBar.register')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </TopBarWrapper>
  );
});

export const TopBarWrapper = styled.div`
  &.top-bar-wrapper {
    background-color: #072232;
    height: 32px;
    .bar-layout-width {
      max-width: 1400px;
      margin: 0 auto;
    }
    .top-bar-inner-wrapper {
      display: flex;
      justify-content: space-between;
    }
    .top-bar-list {
      list-style: none;
      display: flex;
      margin: 0;
    }
    .auth-buttons {
      display: flex;
      height: 32px;
      min-width: 234px;
    }
    .auth-link-button {
      color: ${({ theme }) => theme.colorPalette.white};
      cursor: pointer;
      line-height: 32px;
      padding: 0 12px;
      text-transform: uppercase;
      font-size: ${({ theme }) => theme.fontSize.base};
      font-weight: 700;
    }
    .bz-pro-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 12px;
      border-radius: 3px;
      height: 26px;
      min-width: 160px;
      color: white;
      font-weight: semi-bold;
      background-color: #0075cd;
      margin: 3px 3px 0px 0px;
    }
    .bz-top-bar-list-item-label {
      color: white;
      font-size: 12px;
      line-height: 30px;
      margin: 0 8px;
      white-space: nowrap;
    }
    .contributor-link {
      align-items: center;
      color: white;
      display: inline-flex;
      font-size: ${({ theme }) => theme.fontSize.sm};
      height: 32px;
      margin: 0 12px;
      a {
        color: white;
      }
    }
    .bz-top-bar-list-item {
      color: ${({ theme }) => theme.colorPalette.white};
      text-transform: uppercase;
      line-height: 28px;
      font-weight: 500;
      font-size: ${({ theme }) => theme.fontSize.base};
      margin: 0;
      text-align: center;

      &.ProLabel {
        border-top: none;
        text-transform: none;
        min-width: 140px;
      }

      &:nth-child(2) {
        min-width: 84px;
      }

      &:nth-child(3) {
        min-width: 66px;
      }

      &:nth-child(4) {
        min-width: 83px;
      }

      &:nth-child(5) {
        min-width: 93px;
      }

      &:nth-child(6) {
        min-width: 59px;
      }

      &:nth-child(7) {
        min-width: 86px;
      }
    }
  }
`;
